<div *ngIf="isLoading" class="loader-wrapper">
    <mat-spinner diameter="40"></mat-spinner>
</div>


<div [ngClass]="{ 'prescription-dialog': isDialog }">
    <!-- Top Actions -->
    <div class="prescription-actions m-b-16 m-t-10" *ngIf="!isLoading">
        <button mat-icon-button *ngIf="canPrint" class="d-flex align-items-center" (click)="onPrintPrescription()"
            matTooltip="{{ 'BUTTON.BTN_PRINT' | translate }}">
            <i-tabler name="printer" class="text-primary icon-30 d-flex"></i-tabler>
        </button>
        <button mat-stroked-button color="primary" *ngIf="canAddTemplate" (click)="OnPrescriptionTemplate()">
            {{ 'BUTTON.BTN_ADD_TEMPLATE' | translate }}
        </button>
    </div>

    <!-- Diagnosis -->
    <ng-container *ngIf="!isLoading">
        <form [formGroup]="prescriptionForm">
            <mat-form-field appearance="outline" class="w-100 hide-hint m-b-16">
                <mat-label>{{ 'LABEL.DIAGNOSIS' | translate }}</mat-label>
                <textarea matInput formControlName="diagnosis" readonly></textarea>
            </mat-form-field>
        </form>

        <!-- Template Search + Actions -->
        <div>
            <div class="template-toolbar m-b-16">
                <mat-form-field appearance="outline" class="w-100 hide-hint template-search">
                    <mat-label>{{ 'ENTITY.PRESCRIPTION_TEMPLATE' | translate }}</mat-label>
                    <input matInput type="text" [formControl]="templateCtrl" [matAutocomplete]="templateAuto"
                        placeholder="{{ 'LABEL.SEARCH_LABEL' | translate }}" />
                    <mat-autocomplete #templateAuto="matAutocomplete"
                        (optionSelected)="onTemplateSelected($event.option.value)" [displayWith]="displayTemplateName">
                        <mat-option *ngFor="let template of filteredTemplates" [value]="template">
                            {{ template.name }}
                        </mat-option>
                    </mat-autocomplete>
                </mat-form-field>
            </div>

            <!-- Prescription Table -->
            <div class="table-responsive table-scroll-container">
                <table class="table table-hover b-1 rounded w-100">
                    <thead>
                        <tr>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.DRUG_NAME' | translate }}</th>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.DRUG_PATTERN' | translate }}</th>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.DURATION' | translate }}</th>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.MEAL_RELATION' | translate }}</th>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.INSTRUCTION' | translate }}</th>
                            <th class="p-5 f-s-14 f-w-600">{{ 'LABEL.ACTION_LABEL' | translate }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of prescriptionRows.controls; track row; let index = $index; let last = $last) {
                        <tr [formGroup]="$any(row)">
                            <!-- Drug Name with Autocomplete -->
                            <td class="p-5">
                                <mat-form-field appearance="outline" class="w-100 hide-hint">
                                    <input type="text" matInput formControlName="drugName" [matAutocomplete]="autoDrug"
                                        (blur)="onRowComplete(index)" />
                                    <mat-autocomplete #autoDrug="matAutocomplete"
                                        (optionSelected)="onDrugSelected($event, index)" [displayWith]="displayDrug">
                                        <mat-option class="add-new-option" (click)="openAddDrugDialogInline(index)">
                                            <span class="f-w-600 f-s-15">
                                                {{ 'LABEL.ADD_LABEL' | translate }} New {{ 'ENTITY.DRUG' | translate }}
                                            </span>
                                        </mat-option>
                                        <mat-option *ngFor="let drug of rowFilteredDrugs$[index] | async"
                                            [value]="drug">
                                            {{ drug.name }}
                                        </mat-option>
                                    </mat-autocomplete>
                                </mat-form-field>
                            </td>

                            <!-- Dose Pattern with Directive -->
                            <td class="p-5">
                                <mat-form-field appearance="outline" class="w-100 hide-hint">
                                    <input matInput formControlName="dosePattern" appDosePattern [times]="4"
                                        (suggestionsChange)="dosePatternSuggestions[index] = $event"
                                        [matAutocomplete]="autoDose" (blur)="onRowComplete(index)" />
                                    <mat-autocomplete #autoDose="matAutocomplete">
                                        <mat-option *ngFor="let pattern of dosePatternSuggestions[index]"
                                            [value]="pattern">
                                            {{ pattern }}
                                        </mat-option>
                                    </mat-autocomplete>
                                </mat-form-field>
                            </td>

                            <!-- Duration with Suggestions -->
                            <td class="p-5">
                                <mat-form-field appearance="outline" class="w-100 hide-hint">
                                    <input matInput formControlName="duration" [matAutocomplete]="autoDuration"
                                        (input)="onInlineDurationInput($event, index)" (blur)="onRowComplete(index)"
                                        maxlength="20" />
                                    <mat-autocomplete #autoDuration="matAutocomplete">
                                        <mat-option *ngFor="let suggestion of durationSuggestions[index]"
                                            [value]="suggestion">
                                            {{ suggestion }}
                                        </mat-option>
                                    </mat-autocomplete>
                                </mat-form-field>
                            </td>

                            <!-- Meal Relation Dropdown -->
                            <td class="p-5">
                                <mat-form-field appearance="outline" class="w-100 hide-hint">
                                    <mat-select formControlName="mealRelation" (selectionChange)="onRowComplete(index)">
                                        <mat-option *ngFor="let meal of mealRelationList" [value]="meal">
                                            {{ mealRelationData[meal] }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </td>

                            <!-- Instruction -->
                            <td class="p-5">
                                <mat-form-field appearance="outline" class="w-100 hide-hint">
                                    <input matInput formControlName="instruction" (blur)="onRowComplete(index)"
                                        maxlength="4000" />
                                </mat-form-field>
                            </td>

                            <!-- Actions -->
                            <td class="p-5 whitespace-nowrap">
                                <button mat-icon-button color="warn" *ngIf=" prescriptionRows.length > 1 &&
                            (row.get('drugName')?.value && row.get('dosePattern')?.value &&
                            row.get('duration')?.value)" (click)="onDeleteRow(index)">
                                    <i-tabler name="trash" class="icon-18"></i-tabler>
                                </button>
                            </td>
                        </tr>
                        }
                    </tbody>
                </table>
                <div *ngIf="showEmptyState">
                    <p class="text-center f-s-16 m-t-20 text-muted">{{ 'LABEL.START_TYPING_DRUG' | translate }}</p>
                </div>
            </div>
        </div>
    </ng-container>
</div>

<mat-dialog-actions>
    <div class="action-buttons m-r-10" *ngIf="isDialog">
        <a mat-stroked-button color="primary" (click)="onCloseDialog()">
            {{ 'BUTTON.BTN_CLOSE' | translate }}
        </a>
    </div>
    <div [class]="isDialog ? 'action-buttons' : 'd-flex justify-content-end gap-8 m-t-16 m-r-10'" *ngIf="canSave">
        <button mat-flat-button color="primary" (click)="onSaveAndUpdate()">
            {{ 'BUTTON.BTN_SAVE' | translate }}
        </button>
    </div>
</mat-dialog-actions>